import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { Container, Section } from '@components';
import { format } from 'date-fns';
import { Copy, Check, Download, ExternalLink, CheckCircle2 } from 'lucide-react';

const PLATFORM_COLORS = {
  instagram: 'bg-pink-100 text-pink-700',
  twitter:   'bg-sky-100 text-sky-700',
  linkedin:  'bg-blue-100 text-blue-700',
  facebook:  'bg-indigo-100 text-indigo-700',
  tiktok:    'bg-gray-100 text-gray-700',
};

function useCopy() {
  const [copied, setCopied] = useState(null);
  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };
  return { copied, copy };
}

async function downloadImage(url, filename) {
  try {
    const res  = await fetch(url);
    const blob = await res.blob();
    const a    = document.createElement('a');
    a.href     = URL.createObjectURL(blob);
    a.download = filename || 'media';
    a.click();
    URL.revokeObjectURL(a.href);
  } catch {
    // Fallback: open in new tab
    window.open(url, '_blank');
  }
}

function isVideo(url) {
  if (!url) return false;
  const clean = url.split('?')[0].toLowerCase();
  return /\.(mp4|mov|webm|ogg|avi|mkv)$/.test(clean);
}

const Posts = () => {
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);
  const { copied, copy }      = useCopy();

  useEffect(() => {
    const q = query(
      collection(db, 'social_posts'),
      where('visible', '!=', false),
    );
    getDocs(q)
      .then(snap => {
        const list = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const aTs = a.createdAt?.toDate?.() ?? new Date(0);
            const bTs = b.createdAt?.toDate?.() ?? new Date(0);
            return bTs - aTs; // latest first
          });
        setPosts(list);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-neutral-soft-gray min-h-screen">
      <Section>
        <Container>
          <h1 className="text-h2 text-primary-navy mb-2">Social Posts</h1>
          <p className="text-body text-neutral-medium-gray mb-8">Latest updates across our platforms.</p>

          {loading && (
            <div className="text-center py-16 text-neutral-medium-gray">Loading…</div>
          )}

          {!loading && posts.length === 0 && (
            <div className="text-center py-16 text-neutral-medium-gray">No posts yet.</div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map(post => {
              const scheduledDate = post.scheduledAt?.toDate?.();
              const platform = post.platform || 'other';

              // Build the full ready-to-post text
              const hashtagLine = post.hashtags?.length > 0
                ? '\n\n' + post.hashtags.map(h => `#${h}`).join(' ')
                : '';
              const urlLine = post.url ? '\n\n' + post.url : '';
              const fullText = (post.content || '') + hashtagLine + urlLine;
              const mediaIsVideo = isVideo(post.mediaUrl);
              const downloadName = `post-${post.id}.${mediaIsVideo ? 'mp4' : 'jpg'}`;

              return (
                <div key={post.id} className="bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
                  {post.mediaUrl && (
                    <div className="relative group">
                      {mediaIsVideo ? (
                        <video
                          src={post.mediaUrl}
                          controls
                          muted
                          loop
                          playsInline
                          className="w-full h-48 object-cover bg-black"
                        />
                      ) : (
                        <img
                          src={post.mediaUrl}
                          alt=""
                          className="w-full h-48 object-cover"
                        />
                      )}
                      {/* Media action buttons — visible on hover */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end justify-end gap-2 p-2 pointer-events-none group-hover:pointer-events-auto">
                        <a
                          href={post.mediaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="opacity-0 group-hover:opacity-100 flex items-center gap-1 px-2.5 py-1.5 bg-white/90 hover:bg-white text-gray-800 rounded-lg text-xs font-medium transition-all"
                          title="Open in new tab"
                        >
                          <ExternalLink size={11} /> Open
                        </a>
                        <button
                          onClick={() => downloadImage(post.mediaUrl, downloadName)}
                          className="opacity-0 group-hover:opacity-100 flex items-center gap-1 px-2.5 py-1.5 bg-primary-navy hover:bg-blue-800 text-white rounded-lg text-xs font-medium transition-all"
                          title={`Download ${mediaIsVideo ? 'video' : 'image'} to upload to X / Facebook`}
                        >
                          <Download size={11} /> Download
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="p-5 flex flex-col flex-1">
                    {/* Header row */}
                    <div className="flex items-center justify-between mb-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${PLATFORM_COLORS[platform] ?? 'bg-gray-100 text-gray-600'}`}>
                        {platform}
                      </span>
                      {scheduledDate && (
                        <span className="text-xs text-neutral-medium-gray">
                          {format(scheduledDate, 'MMM d, yyyy')}
                        </span>
                      )}
                    </div>

                    {/* Post text */}
                    <p className="text-sm text-neutral-charcoal leading-relaxed flex-1 whitespace-pre-wrap">{post.content}</p>

                    {post.hashtags?.length > 0 && (
                      <p className="text-xs text-blue-400 mt-2">
                        {post.hashtags.map(h => `#${h}`).join(' ')}
                      </p>
                    )}

                    {/* Posted to X indicator */}
                    {post.postedToX && (
                      <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-600">
                        <CheckCircle2 size={13} className="shrink-0" />
                        <span>
                          Posted to X
                          {post.postedAt && (
                            <> · {format(post.postedAt.toDate(), 'MMM d, yyyy h:mm a')}</>
                          )}
                        </span>
                      </div>
                    )}

                    {/* URL row */}
                    {post.url && (
                      <div className="mt-3 flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                        <a
                          href={post.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 truncate flex-1 hover:underline"
                        >
                          {post.url}
                        </a>
                        <button
                          onClick={() => copy(post.url, post.id + '_url')}
                          className="shrink-0 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Copy URL"
                        >
                          {copied === post.id + '_url' ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
                        </button>
                      </div>
                    )}

                    {/* Copy full post button */}
                    <button
                      onClick={() => copy(fullText, post.id + '_text')}
                      className={`mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                        copied === post.id + '_text'
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-primary-navy text-white hover:bg-blue-800'
                      }`}
                    >
                      {copied === post.id + '_text'
                        ? <><Check size={14} /> Copied to clipboard!</>
                        : <><Copy size={14} /> Copy full post</>
                      }
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </Container>
      </Section>
    </div>
  );
};

export default Posts;
